import { useTranslation } from "react-i18next";

import type { Outcome, SideLabel, SideView } from "../../services/generated/client.js";
import { cn } from "../../lib/utils.js";
import { CoinPurse } from "./CoinPurse.js";
import { GuessClaim } from "./GuessClaim.js";
import { Timeline, type TakenSlot } from "./Timeline.js";

/** How big the surface rendering this is. */
export type Scale =
  /** A phone or a laptop beside a video call — arm's length. */
  | "near"
  /** A television across the room — three metres, no pointer. */
  | "room";

/**
 * One side of the board: who they are, what they hold, and what they are doing.
 *
 * # Why this is a shared component and not two
 *
 * The television and the phone render the same six facts, and until phase 7 they
 * rendered them twice — which is how the display ended up showing *"Playing
 * now"* over the active side and an empty string over everybody else while the
 * phone said *"Another side"*. Two implementations of one thing disagree; the
 * question is only when.
 *
 * `scale` chooses type sizes and nothing else. It is not a capability switch:
 * both surfaces are read-only here, and a board a viewer can act on is a
 * `Timeline` with an `onPlace`, which is the caller's business.
 *
 * # Everything is in words
 *
 * Whose turn it is, who has placed, who is out, which side is yours. None of it
 * is signalled by colour alone — required on the display by
 * `.claude/rules/frontend.md`, and independently right on a phone in a dark room
 * at a party.
 *
 * # A side with no label has left
 *
 * `label` is `null` when the roster does not name this side, which happens for
 * exactly one reason: the player left the party while their timeline stayed in
 * the game. Saying so is better than a blank — see [`sideLabel`](../../lib/roster.js).
 */
export function SideBoard({
  side,
  label,
  active,
  yours = false,
  cardsToWin,
  claimedSlot = null,
  takenSlots = [],
  outcome = null,
  wonByChallenge = false,
  guess = null,
  scale = "near",
  className,
}: {
  /** The side, as the board topic carries it. */
  side: SideView;
  /** Who they are. `null` when they have left the party. */
  label: SideLabel | null;
  /** Whether this side's placement is on the table. */
  active: boolean;
  /** Whether this is the viewer's own side. */
  yours?: boolean;
  /** Cards on a completed timeline — draws the progress readout. */
  cardsToWin?: number;
  /**
   * The gap the active side claims the card in flight belongs in, from the
   * challenge window onward. `null` on every side that is not the active one.
   *
   * ⚠️ **This is a claim, not the answer, and it is safe before `Reveal`** —
   * unlike `outcome` above. A slot index says where the active side asserts the
   * card goes; it says nothing about the year, which is what Critical Rule 7
   * protects. Phase 16a: without this the challenge prompt asked people to
   * contest a placement no screen had ever drawn.
   */
  claimedSlot?: number | null;
  /**
   * Gaps on this side's timeline that a challenger has claimed.
   *
   * **Phase 18, finding #9**: *"when someone challenges, the active player
   * doesn't know where the challenges are — it should be visible."* This has
   * been on the wire since 16b and was drawn in exactly two places, both inside
   * the challenger's own panel — so the active player saw nothing, and neither
   * did the television.
   *
   * ⚠️ A claim, like `claimedSlot`, and safe for the same reason.
   */
  takenSlots?: readonly TakenSlot[];
  /**
   * How this side did, once the round has revealed. `null` before that.
   *
   * ⚠️ **Never ask for this before `Reveal`.** The server does not send it — the
   * engine leaves `RoundView.outcomes` empty until the answer is public,
   * deliberately, because "you got the year right" *is* the year (Critical Rule
   * 7). The prop is narrow for the same reason every prop here is: a component
   * handed a wider object than it renders is how the answer leaks.
   */
  outcome?: Outcome | null;
  /**
   * Whether this side took the card by **challenging** rather than by placing.
   *
   * **Phase 18, finding #13**: *"another player challenged and was right on the
   * year, but it said falló and gave him the card anyway."* The verdict was
   * drawn from `outcome.slot_correct`, which describes a placement a challenger
   * never made and is therefore always `false` for them — so the row read
   * *"Falló … Ganó la carta"*, two true answers to two questions and neither of
   * them the one being asked.
   *
   * ⚠️ **Do not infer this from `wins_card`.** 16j-2 recorded why when the same
   * value was written to `rounds`: in a concurrent mode several sides win cards
   * in one round. It comes from `RoundView.challenge_won_by`, which the engine
   * writes once, in the only place that knows.
   */
  wonByChallenge?: boolean;
  /**
   * The options this side picked, from the challenge window onward.
   *
   * **Phase 18, finding #1**, and the most important thing the third real party
   * said: the room could see which *slot* the active side chose and never what
   * they **answered**, so nobody could judge the guess — *"we can't challenge
   * the active player's guess"*. It was missing after `Reveal` too, which is the
   * half with nothing to argue about.
   *
   * ⚠️ **A claim, not the answer.** These are indices into the grids, which say
   * what somebody picked and never which pick is right. Critical Rule 7 is about
   * the second. Pass `correctOptions` only once the round has revealed.
   */
  guess?: { artist: string | null; title: string | null } | null;
  /** How far away the reader is. */
  scale?: Scale;
  className?: string;
}) {
  const { t } = useTranslation();
  const room = scale === "room";
  const name = label?.name ?? t("side.left");
  // One member is the same string as `name` (the server sends it that way in
  // Classic and Deathmatch on purpose), so repeating it would put every player's
  // name on screen twice. More than one is a team, and then it is the roster.
  const teammates = (label?.members.length ?? 0) > 1 ? label?.members : null;

  return (
    <article
      data-testid="side-board"
      aria-label={name}
      className={cn(
        "min-w-0 rounded-2xl border-2",
        room ? "p-5" : "p-3",
        active ? "border-flash-500 bg-flash-500/5" : "border-white/10",
        (side.eliminated || side.absent) && "opacity-40",
        className,
      )}
    >
      {/* ⚠️ `items-center`, not `items-baseline`. The purse is a 24px mark on a
          40px television one and the name is a line of text: aligning their
          *baselines* hangs the coin below the name and reads as two things that
          missed each other. They are one row and they centre on it.

          ⚠️ **The coin is the last thing in the row**, after the status —
          *"the coin icon should be always to the right"*. It is the number
          every other screen puts in a corner, so it holds this one's. */}
      <header
        className={cn(
          "mb-3 flex items-center justify-between gap-4",
          room ? "text-xl" : "text-sm",
        )}
      >
        <h3 className={cn("min-w-0 truncate font-semibold", room && "text-2xl")}>
          {name}
          {yours && <span className="text-flash-400"> {t("side.yours")}</span>}
        </h3>
        <span className="flex shrink-0 items-center gap-3">
          <Status side={side} active={active} />
          <CoinPurse coins={side.coins} size={room ? "room" : "near"} />
        </span>
      </header>

      {teammates && (
        <p className={cn("mb-2 truncate text-white/50", room ? "text-lg" : "text-xs")}>
          {teammates.join(", ")}
        </p>
      )}

      {/* **The 23-second gap this closes.** A card does not reach a timeline
          until `Settled` (ADR 0009: nothing between `Reveal` and `Settled` is
          real, which is what makes a void free), so a correct guess used to be
          invisible to everybody else through the challenge, reveal and dispute
          windows. `outcomes` has been on the wire since `Reveal` the whole time
          and no client rendered it. */}
      {outcome && (
        <p
          className={cn(
            "mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 font-medium",
            room ? "text-xl" : "text-xs",
          )}
        >
          {/* **Three verdicts, not two** — phase 18, finding #13. Winning by
              challenging is its own outcome: the challenger placed nothing, so
              `slot_correct` is a fact about a move they never made. */}
          <span
            className={
              wonByChallenge || outcome.slot_correct
                ? "text-correct-500"
                : "text-wrong-500"
            }
          >
            {wonByChallenge
              ? t("round.outcome.wonByChallenge")
              : outcome.slot_correct
                ? t("round.outcome.right")
                : t("round.outcome.wrong")}
          </span>
          {outcome.coins_awarded > 0 && (
            <span className="text-coin-400">
              {t("round.outcome.coins", { count: outcome.coins_awarded })}
            </span>
          )}
          <span className="text-white/60">
            {outcome.wins_card ? t("round.outcome.winsCard") : t("round.outcome.noCard")}
          </span>
          {/* What they actually got, so a split score reads as a split score
              rather than as one verdict. Scoring is deliberately per-field —
              knowing the artist but not the song still pays, and that is most
              of what keeps a weaker player in the game. */}
          <span className="text-white/40">
            {[
              outcome.artist_correct ? t("round.outcome.artist") : null,
              outcome.title_correct ? t("round.outcome.title") : null,
              outcome.exact_year_correct ? t("round.outcome.year") : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </p>
      )}

      {/* **What they said it was** — phase 18, finding #1. A claim: what somebody
          picked, never which pick is right. It appears when the challenge window
          opens, which is the moment it becomes possible to disagree with, and it
          stays through the reveal, which is when the room finds out who was
          right. Before that the server does not send it at all. */}
      {guess && (
        <GuessClaim
          artist={guess.artist}
          title={guess.title}
          scale={scale}
          className="mb-3"
        />
      )}

      {/* ⚠️ **No `label`, deliberately.** It used to pass `"3 cards"`, which the
          header then drew next to `3 / 10` — the same number twice. The count
          is the progress readout's job now, and it is drawn as a card. The
          section keeps an accessible name from `Timeline`'s own default, inside
          an article that is already labelled with whose board this is. */}
      <Timeline
        cards={side.timeline}
        cardCount={side.card_count}
        hidden={side.timeline_hidden}
        claimedSlot={claimedSlot}
        takenSlots={takenSlots}
        claimedBy={label?.name ?? null}
        scale={scale}
        {...(cardsToWin === undefined ? {} : { cardsToWin })}
      />
    </article>
  );
}

/**
 * What this side is doing, as a sentence fragment.
 *
 * Ordered by what a player needs first: being out ends the others, and whose
 * turn it is outranks whether they have finished their move.
 */
function Status({ side, active }: { side: SideView; active: boolean }) {
  const { t } = useTranslation();

  if (side.eliminated) {
    return <span className="text-white/50">{t("side.status.out")}</span>;
  }
  // **Not the same word as eliminated, and the order matters.** Being knocked
  // out is earned; a dead phone is not, and a screen that told the room somebody
  // was eliminated when their battery died would be saying something untrue
  // about a game they are watching. Checked after `eliminated` because a side
  // that is both is out for the reason it earned.
  if (side.absent) {
    return <span className="text-white/50">{t("side.status.away")}</span>;
  }
  if (side.has_placed) {
    return <span className="text-correct-500">{t("side.status.placed")}</span>;
  }
  if (active) {
    return <span className="text-flash-400">{t("side.status.playing")}</span>;
  }
  return null;
}
